import { z } from "../zod.js";

// Module 06 — Inventory domain events (spec 06 §6). The typed catalog of the
// 19 PRODUCER events this module emits (past-tense two-segment names,
// `inventory.subject_pastverb`, D-015) + their payload shapes, plus the set of
// events this module CONSUMES. Emitters write to the transactional outbox in
// the same txn as the mutation (the events rule); the envelope
// (event_id, tenant_id, occurred_at, actor) is added centrally by OutboxService.
//
// Mirrors events/onboarding.ts: this is the contracts-layer home foreseen by
// D-015 / TESTING §5.2 — intentionally NOT registered into the OpenAPI
// document (events are not HTTP routes), so it adds zero paths/schemas to
// openapi/v1.json. Phase 2's inventory event emitter imports these payload
// schemas so every emit site is typed against the same contract the §6
// consumers parse.
//
// EXACTNESS (D-041/D-042): every quantity/measure travels as a DECIMAL STRING
// (the numeric(18,6) text Postgres returns), never a JSON number — a float on
// the wire is precisely what the exact-arithmetic doctrine keeps out of the
// ledger. Money is integer minor units + char(3) currency.

/** A `numeric(18,6)` quantity/measure as its exact decimal text (D-041). */
const Measure = z.string().regex(/^-?\d{1,12}(\.\d{1,6})?$/);
/** Integer minor units (money) — never a float (the money convention). */
const MinorMoney = z.number().int();
const Uuid = z.guid();
const Currency = z.string().length(3);

// --- producer payloads (spec 06 §6 table, in order) ---

export const InventoryMovementRecordedPayload = z.object({
  movement_id: Uuid,
  movement_type: z.string(),
  product_id: Uuid.nullable(),
  lot_id: Uuid.nullable(),
  stock_unit_id: Uuid.nullable(),
  from_location_id: Uuid,
  to_location_id: Uuid,
  qty_base: Measure,
  uom: z.string(),
  unit_cost_minor: MinorMoney.nullable(),
  currency: Currency.nullable(),
  source_doc_type: z.string().nullable(),
  source_doc_id: Uuid.nullable(),
});

export const InventoryLotCreatedPayload = z.object({
  lot_id: Uuid,
  product_id: Uuid,
  origin: z.string(),
  supplier_id: Uuid.nullable(),
  work_order_id: Uuid.nullable(),
  qc_status: z.string(),
});

/** Shared by lot_quarantined / lot_released (spec 06 §6 — one payload row). */
export const InventoryLotQcChangedPayload = z.object({
  lot_id: Uuid,
  product_id: Uuid,
  reason: z.string().nullable(),
  affected_qty_base: Measure,
  affected_unit_ids: z.array(Uuid),
});

export const InventoryStockUnitCreatedPayload = z.object({
  stock_unit_id: Uuid,
  serial_code: z.string(),
  kind: z.string(),
  product_id: Uuid.nullable(),
  lot_id: Uuid.nullable(),
  capacity_measure: Measure.nullable(),
  initial_measure: Measure.nullable(),
  claimed_measure: Measure.nullable(),
  location_id: Uuid.nullable(),
  // §9.4: operator skipped measuring → initial := claimed, flagged here. An
  // event-payload fact only (no persisted column — amended 2026-08-01, fix
  // round): consumers treat the later `unit_remeasured` as the verification.
  unverified_measure: z.boolean().optional(),
});

export const InventoryUnitCutPayload = z.object({
  parent_stock_unit_id: Uuid,
  // The parent's PRODUCT IDENTITY, captured inside the cut transaction BEFORE
  // §3.2 step 4 disposes of the source row (spec 06 §6 amendment 2026-08-09):
  // an exhausted returnable parent flips to `empty` with `product_id`,
  // `lot_id` and `measure_uom` NULLed in the SAME transaction, so module 05's
  // door-4 consumer — which runs after commit — cannot re-read the identity it
  // needs to price the `waste_pieces[]` trim entries (or the children, whose
  // product/lot/facility are by construction the parent's). Optional on the
  // wire ONLY for envelopes appended before this amendment; the producer
  // always emits all four, and the consumer refuses LOUDLY when neither the
  // payload nor the surviving row can answer.
  parent_product_id: Uuid.optional(),
  parent_lot_id: Uuid.nullable().optional(),
  parent_facility_id: Uuid.optional(),
  parent_measure_uom: z.string().nullable().optional(),
  children: z.array(
    z.object({
      stock_unit_id: Uuid,
      serial_code: z.string(),
      length: Measure,
      is_offcut: z.boolean(),
      quarantined: z.boolean(),
    }),
  ),
  // §3.2: requested trim windows AND the step-4 tolerance write-off — every
  // piece Module 05's trim-waste ledger must book. The reason vocabulary is
  // the kernel plan's (§6 row, amended 2026-08-01 fix round); `cut_tolerance`
  // is the write-off's movement reason_code, never this field.
  waste_pieces: z.array(
    z.object({
      length: Measure,
      reason: z.enum(["requested_waste", "kerf", "tolerance_writeoff"]),
    }),
  ),
  reason: z.string().nullable(),
  work_order_id: Uuid.nullable().optional(),
  location_id: Uuid.nullable(),
});

export const InventoryUnitRemeasuredPayload = z.object({
  stock_unit_id: Uuid,
  previous_measure: Measure.nullable(),
  entered_measure: Measure,
  delta: Measure,
  adjustment_movement_id: Uuid.nullable(),
});

export const InventoryUnitScannedPayload = z.object({
  scan_event_id: Uuid,
  stock_unit_id: Uuid,
  action: z.string(),
  location_id: Uuid.nullable(),
  facility_id: Uuid,
  performed_by: Uuid,
});

export const InventoryUnitFoundOffsitePayload = z.object({
  stock_unit_id: Uuid,
  expected_facility_id: Uuid,
  found_facility_id: Uuid,
  found_location_id: Uuid.nullable(),
});

export const InventoryTransferDispatchedPayload = z.object({
  transfer_id: Uuid,
  from_facility_id: Uuid,
  to_facility_id: Uuid,
  lines: z.array(
    z.object({
      product_id: Uuid.nullable(),
      lot_id: Uuid.nullable(),
      stock_unit_id: Uuid.nullable(),
      qty_base: Measure,
    }),
  ),
  eta_on: z.string().nullable(), // ISO date (facility timezone)
});

export const InventoryTransferReceivedPayload = z.object({
  transfer_id: Uuid,
  received_lines: z.array(z.object({ line_id: Uuid, received_qty_base: Measure })),
  complete: z.boolean(),
});

export const InventoryTransferReceiveVariancePayload = z.object({
  transfer_id: Uuid,
  line_id: Uuid,
  product_id: Uuid.nullable(),
  expected_qty_base: Measure,
  received_qty_base: Measure,
  // NULLABLE per edge case 14: an unvalued shortfall says so rather than
  // inventing a number ("unvalued" routes to the top approval tier).
  shortfall_value_minor: MinorMoney.nullable(),
  currency: Currency.nullable(),
  variance_reason: z.string(),
});

export const InventoryCountSubmittedPayload = z.object({
  cycle_count_id: Uuid,
  variance_value_minor: MinorMoney.nullable(),
  currency: Currency.nullable(),
  line_count: z.number().int().min(0),
  variance_line_count: z.number().int().min(0),
});

export const InventoryCountPostedPayload = z.object({
  cycle_count_id: Uuid,
  adjustments: z.array(
    z.object({
      movement_id: Uuid,
      product_id: Uuid,
      variance_qty_base: Measure,
      variance_value_minor: MinorMoney.nullable(),
    }),
  ),
});

/** Shared by reservation_created / reservation_released (spec 06 §6). */
export const InventoryReservationChangedPayload = z.object({
  reservation_id: Uuid,
  product_id: Uuid,
  qty_base: Measure,
  demand_type: z.string(),
  demand_id: Uuid,
});

export const InventoryGenealogyEdgeAddedPayload = z.object({
  edge_id: Uuid,
  parent_kind: z.enum(["lot", "stock_unit"]),
  parent_id: Uuid,
  child_kind: z.enum(["lot", "stock_unit"]),
  child_id: Uuid,
  relation: z.string(),
  work_order_id: Uuid.nullable(),
  qty: Measure,
});

export const InventoryLevelDriftDetectedPayload = z.object({
  product_id: Uuid,
  location_id: Uuid,
  cached_on_hand: Measure,
  replayed_on_hand: Measure,
});

export const InventoryLocationUpdatedPayload = z.object({
  location_id: Uuid,
  code: z.string(),
  path: z.string(),
  kind: z.string(),
  is_active: z.boolean(),
  changed_fields: z.array(z.string()),
});

/**
 * The 19 producer events (spec 06 §6) keyed by name → payload schema, with the
 * declared consumer module ids. The keys ARE the wire event names; the D-015
 * reconcile pairs each producer against its consumers' subscriptions.
 */
export const inventoryProducerEvents = {
  "inventory.movement_recorded": {
    payload: InventoryMovementRecordedPayload,
    consumers: ["11", "03", "13"],
  },
  "inventory.lot_created": {
    payload: InventoryLotCreatedPayload,
    consumers: ["07", "13"],
  },
  "inventory.lot_quarantined": {
    payload: InventoryLotQcChangedPayload,
    consumers: ["03", "12", "09"],
  },
  "inventory.lot_released": {
    payload: InventoryLotQcChangedPayload,
    consumers: ["03", "12", "09"],
  },
  "inventory.stock_unit_created": {
    payload: InventoryStockUnitCreatedPayload,
    consumers: ["04", "13"],
  },
  "inventory.unit_cut": {
    payload: InventoryUnitCutPayload,
    consumers: ["05", "07", "13"],
  },
  "inventory.unit_remeasured": {
    payload: InventoryUnitRemeasuredPayload,
    consumers: ["04", "13"],
  },
  "inventory.unit_scanned": {
    payload: InventoryUnitScannedPayload,
    consumers: ["09", "13"],
  },
  "inventory.unit_found_offsite": {
    payload: InventoryUnitFoundOffsitePayload,
    consumers: ["12"],
  },
  "inventory.transfer_dispatched": {
    payload: InventoryTransferDispatchedPayload,
    consumers: ["03", "05", "13"],
  },
  "inventory.transfer_received": {
    payload: InventoryTransferReceivedPayload,
    consumers: ["03", "05", "13"],
  },
  "inventory.transfer_receive_variance": {
    payload: InventoryTransferReceiveVariancePayload,
    consumers: ["12", "11"],
  },
  "inventory.count_submitted": {
    payload: InventoryCountSubmittedPayload,
    consumers: ["12"],
  },
  "inventory.count_posted": {
    payload: InventoryCountPostedPayload,
    consumers: ["11", "13", "03"],
  },
  // D-391 ruling 2 (P3-H-3): the `"03"` these rows carried named no
  // subscriber and is dropped; module 09 consumes `reservation_created` for
  // the system `→ ready` (spec 06 §6's row).
  "inventory.reservation_created": {
    payload: InventoryReservationChangedPayload,
    consumers: ["09"],
  },
  // P3-J J-3 (D-466 ruling 1): module 03's promise watch arms its debounce
  // for a release of a hold a live promise reserved or stands on.
  "inventory.reservation_released": {
    payload: InventoryReservationChangedPayload,
    consumers: ["09", "03"],
  },
  "inventory.genealogy_edge_added": {
    payload: InventoryGenealogyEdgeAddedPayload,
    consumers: ["07", "13"],
  },
  "inventory.level_drift_detected": {
    payload: InventoryLevelDriftDetectedPayload,
    consumers: ["12", "15"],
  },
  "inventory.location_updated": {
    payload: InventoryLocationUpdatedPayload,
    consumers: ["18", "13"],
  },
} as const;

export type InventoryEventName = keyof typeof inventoryProducerEvents;

export const inventoryProducerEventNames = Object.keys(
  inventoryProducerEvents,
) as InventoryEventName[];

/**
 * Events module 06 CONSUMES (spec 06 §6 "Consumed by this module"). Declared so
 * the D-015 reconcile can pair these subscriptions against their producers'
 * exact names when those producer modules land. Consumption is deduped on
 * event_id via inventory_processed_events (§2.14, D-044; edge case 16), and an
 * invariant-failing apply follows the §6 inbound failure policy (retry → DLQ +
 * Module 12 `event_apply_failed` exception, never silent).
 */
export const inventoryConsumedEventNames = [
  "production.qty_reported", // 04 → §3.1.2 production output receipt
  "production.material_issued", // 04 → issue movements + genealogy + reservation fulfilment
  // 04 → the §2.14 genealogy fact. DECLARED HERE AND DELIBERATELY UNSUBSCRIBED
  // (D-085, 2026-08-06 — the same shape `production.material_issued` above
  // already has): spec 04 §2.14 named module 06 as this event's consumer while
  // spec 06 §6 did not list it at all, and the two are reconciled by ruling
  // that module 06 stays unsubscribed. It already writes the produced-side
  // edges synchronously in `receiveProductionOutput` (D-065), so a consumer
  // would be a second, eventually-consistent writer racing a committed
  // transaction; the consumed-direction edge comes in through
  // `GenealogyService.addEdgeInTxn` called from module 04's own transaction.
  // `outbox-subscriber-parity.test.ts` holds the reason to account.
  "production.serial_lot_captured",
  "quality.inspection_failed", // 07 → quarantine / guided cut
  "waste.regrind_completed", // 05 → reprocessed lot receipt
  "sales.shipment_shipped", // 09 → shipment movements + reservation fulfilment (same txn)
  "procurement.receipt_posted", // 10 → §3.1.1 PO receipt via event
  "approval.decided", // 12 → resolves cycle_counts + adjustment_requests
] as const;
